import { useCallback } from "react";
import {
  RedirectionSideEffect,
  TransformData,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate,
} from "react-admin";

const useUpdateWithValidation = (
  resource: string,
  {
    redirectTo = "list",
    transform,
  }: { redirectTo?: RedirectionSideEffect; transform?: TransformData } = {}
) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const notify = useNotify();
  const [update] = useUpdate();
  const save = useCallback(
    async (values: any) => {
      const transformedValues = await Promise.resolve(
        transform ? transform(values) : values
      );

      try {
        await update(
          resource,
          { data: transformedValues },
          { returnPromise: true, mutationMode: "pessimistic" }
        );
        notify("ra.notification.updated", {
          type: "info",
          messageArgs: { smart_count: 1 },
        });
        redirect(redirectTo, resource, values.id, values);
      } catch (error: any) {
        const fieldErrors = error?.body?.graphQLErrors?.filter(
          (err: any) => err?.extensions?.code
        );
        if (fieldErrors?.length) {
          // Reduce fieldErrors to an object with extensions field as key and code as value
          const validationErrors = fieldErrors.reduce(
            (acc: Object, err: any) => ({
              ...acc,
              [err?.extensions?.field]: translate(
                `error.${err?.extensions?.code ?? "invalid"}`,
                err?.extensions?.arguments
              ),
            }),
            {}
          );
          return validationErrors;
        }
        notify(
          typeof error === "string"
            ? error
            : error.message || "ra.notification.http_error",
          {
            type: "warning",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                  ? error.message
                  : undefined,
            },
          }
        );
      }
    },
    [update, notify, redirect, translate, redirectTo, resource, transform]
  );

  return save;
};

export default useUpdateWithValidation;
